import { apiClient, idempotentConfig, unwrap } from './client';
import type {
  ApiEnvelope,
  InstantiateTemplateRequest,
  InstantiateTemplateResult,
  ServerDiagnostic,
  TemplateDetail,
  TemplateList,
} from './types';

/**
 * B4 — template gallery (F-DS-06 / F-ED-02). Curated + estate-candidate templates, an IR preview,
 * and instantiate (a COPY of the template's IR — variant lineage is a tracked follow-on) → a new
 * DRAFT flow in the PS-2 designer. The SPA degrades to a teaching empty state where the endpoints
 * 404. Templates are hub/JWT-scoped server-side (no `{hub}` path segment in this contract).
 * Instantiate carries `ol-idempotency-key`.
 */

const enc = (s: string) => encodeURIComponent(s);

export interface TemplateQuery {
  category?: string;
  jurisdiction?: string;
}

/** GET /v1/templates?category=&jurisdiction= — the gallery. `{templates:[…], totalCount}`. */
export async function listTemplates(q: TemplateQuery = {}): Promise<TemplateList> {
  const { data } = await apiClient.get<ApiEnvelope<TemplateList>>('/v1/templates', {
    params: { category: q.category, jurisdiction: q.jurisdiction },
  });
  const body = unwrap(data);
  return {
    templates: Array.isArray(body?.templates) ? body.templates : [],
    totalCount: body?.totalCount ?? 0,
  };
}

/** GET /v1/templates/{id} — summary + irPreview + (estate-candidate) diagnostics. */
export async function getTemplate(templateId: string): Promise<TemplateDetail> {
  const { data } = await apiClient.get<ApiEnvelope<TemplateDetail>>(
    `/v1/templates/${enc(templateId)}`,
  );
  const body = unwrap(data);
  return {
    ...body,
    diagnostics: Array.isArray(body?.diagnostics) ? body.diagnostics : undefined,
  };
}

/**
 * POST /v1/templates/{id}/instantiate — materialise the template as a new DRAFT flow (a COPY of
 * its IR; no variant lineage yet). A 422 carries either TEMPLATE_COMPILE_FAILED (with a nested
 * `diagnostics` ValidationResult — the caller renders it as an actionable list; some estate
 * candidates carry non-canonical families) or TEMPLATE_IR_UNAVAILABLE (the legacy corpus is not
 * mounted on this deployment). `extractInstantiateDiagnostics` pulls the diagnostics off the axios
 * error defensively.
 */
export async function instantiateTemplate(
  templateId: string,
  body: InstantiateTemplateRequest,
  idemKey?: string,
): Promise<InstantiateTemplateResult> {
  const { data } = await apiClient.post<ApiEnvelope<InstantiateTemplateResult>>(
    `/v1/templates/${enc(templateId)}/instantiate`,
    body,
    idempotentConfig(idemKey),
  );
  return unwrap(data);
}

/**
 * Best-effort pull of the diagnostics off a thrown 422 (TEMPLATE_COMPILE_FAILED) error body.
 * The wire shape is a NESTED ValidationResult — `diagnostics: {ok, errors: [...], warnings: [...]}`
 * — not a flat array. Errors are returned first, then warnings.
 */
export function extractInstantiateDiagnostics(err: unknown): ServerDiagnostic[] {
  const body = (err as { response?: { data?: unknown } } | undefined)?.response?.data as
    | Record<string, unknown>
    | undefined;
  const diag = body?.diagnostics;
  if (!diag || typeof diag !== 'object') return [];
  if (Array.isArray(diag)) {
    // Defensive: tolerate a flat array should the wire shape ever regress.
    return diag as ServerDiagnostic[];
  }
  const nested = diag as { errors?: unknown; warnings?: unknown };
  const errors = Array.isArray(nested.errors) ? (nested.errors as ServerDiagnostic[]) : [];
  const warnings = Array.isArray(nested.warnings) ? (nested.warnings as ServerDiagnostic[]) : [];
  return [...errors, ...warnings];
}
